/**
 * Stage 2 — the Release Validation Plan.
 *
 * Drafted by the AI when the risks are confirmed; editable by a human (each
 * edit bumps the version and is audited by hash); downloadable as .md / .pdf.
 * "Author tests" hands the plan to stage 3.
 */

import { useState } from "react";
import {
  Alert,
  Button,
  Group,
  Menu,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import {
  IconChecklist,
  IconDeviceFloppy,
  IconDownload,
  IconInfoCircle,
  IconPencil,
  IconRefresh,
  IconX,
} from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { ReportView } from "@/features/ai/ReportView";
import {
  downloadArtefact,
  generatePlan,
  generateTests,
  updatePlan,
  type RunOut,
} from "@/shared/api/validation";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";
import { AdvisoryNote, Provenance } from "./Provenance";

interface Props {
  run: RunOut;
  onUpdated: (run: RunOut, jump?: boolean) => void;
}

export function PlanStage({ run, onUpdated }: Props) {
  const getToken = useAccessToken();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [reason, setReason] = useState("");
  const [existingCases, setExistingCases] = useState("");
  const [busy, setBusy] = useState<"save" | "regen" | "tests" | "dl" | null>(null);

  const canEdit = run.is_owner && ["planned", "authored", "executing"].includes(run.status);
  const canAuthor = run.is_owner && ["planned", "authored"].includes(run.status);

  const save = async () => {
    setBusy("save");
    try {
      onUpdated(await updatePlan(await getToken(), run.run_id, draft, reason.trim()), false);
      setEditing(false);
      toastSuccess("Plan saved", `Now version ${run.plan_version + 1}.`);
    } catch (err) {
      toastApiError("Could not save the plan", err);
    } finally {
      setBusy(null);
    }
  };

  const regen = async () => {
    setBusy("regen");
    try {
      onUpdated(await generatePlan(await getToken(), run.run_id), false);
      toastSuccess("Plan re-drafted", "A new version was generated from the confirmed risks.");
    } catch (err) {
      toastApiError("Could not re-draft the plan", err);
    } finally {
      setBusy(null);
    }
  };

  const author = async () => {
    setBusy("tests");
    try {
      const fresh = await generateTests(await getToken(), run.run_id, existingCases.trim() || null);
      onUpdated(fresh);
      toastSuccess(
        "Tests authored",
        `${fresh.tests.length} test scripts — ${fresh.tests.filter((t) => t.mode === "automatic").length} automatic.`,
      );
    } catch (err) {
      toastApiError("Could not author tests", err);
    } finally {
      setBusy(null);
    }
  };

  const download = async (fmt: "md" | "pdf") => {
    setBusy("dl");
    try {
      const blob = await downloadArtefact(await getToken(), run.run_id, "plan", fmt);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${run.title}-validation-plan.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toastApiError("Could not download", err);
    } finally {
      setBusy(null);
    }
  };

  if (!run.plan_md) {
    return (
      <Alert variant="light" color="blue" icon={<IconInfoCircle size={18} />}>
        No plan yet. Confirm the risk items on the Risk assessment tab — the plan is drafted from
        them.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Text fw={600}>Release Validation Plan · v{run.plan_version}</Text>
          <Provenance promptVersion={run.plan_prompt_version} inferenceId={run.plan_inference_id} />
          <AdvisoryNote />
        </Stack>
        <Group gap="xs">
          <Menu>
            <Menu.Target>
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                loading={busy === "dl"}
              >
                Download
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => void download("pdf")}>PDF</Menu.Item>
              <Menu.Item onClick={() => void download("md")}>Markdown</Menu.Item>
            </Menu.Dropdown>
          </Menu>
          {canEdit && !editing && (
            <Button
              variant="light"
              leftSection={<IconPencil size={16} />}
              onClick={() => {
                setDraft(run.plan_md ?? "");
                setReason("");
                setEditing(true);
              }}
            >
              Edit
            </Button>
          )}
          {canAuthor && run.status === "planned" && (
            <Button
              variant="light"
              leftSection={<IconRefresh size={16} />}
              loading={busy === "regen"}
              onClick={() => void regen()}
            >
              Re-draft
            </Button>
          )}
        </Group>
      </Group>

      {editing ? (
        <Paper withBorder radius="md" p="md">
          <Stack gap="sm">
            <Textarea
              autosize
              minRows={16}
              maxRows={40}
              value={draft}
              onChange={(e) => setDraft(e.currentTarget.value)}
              styles={{
                input: { fontFamily: "var(--mantine-font-family-monospace)", fontSize: 13 },
              }}
            />
            <Group align="flex-end">
              <TextInput
                label="Reason for the change"
                description="Recorded in the audit trail with the plan's before/after hash."
                value={reason}
                onChange={(e) => setReason(e.currentTarget.value)}
                style={{ flex: 1 }}
              />
              <Button
                variant="subtle"
                leftSection={<IconX size={16} />}
                onClick={() => setEditing(false)}
              >
                Cancel
              </Button>
              <Button
                leftSection={<IconDeviceFloppy size={16} />}
                disabled={reason.trim().length < 3 || draft.trim() === run.plan_md}
                loading={busy === "save"}
                onClick={() => void save()}
              >
                Save as v{run.plan_version + 1}
              </Button>
            </Group>
          </Stack>
        </Paper>
      ) : (
        <Paper withBorder radius="md" p="lg">
          <ScrollArea.Autosize mah={720}>
            <ReportView text={run.plan_md} />
          </ScrollArea.Autosize>
        </Paper>
      )}

      {canAuthor && !editing && (
        <Paper withBorder radius="md" p="md" bg="var(--mantine-color-default-hover)">
          <Stack gap="sm">
            <div>
              <Text fw={600}>Re-author the test scripts</Text>
              <Text size="sm" c="dimmed">
                Tests are drafted automatically at gate 1; re-author here to fold in existing
                customer test cases or after editing the plan. Each script is classified manual or
                automatic
                {run.veeva_connection_id
                  ? " (automatic = read-only VQL checks against the connected vault)."
                  : ". No vault on this run, so every test will be manual."}
              </Text>
            </div>
            <Textarea
              label="Existing test cases to reuse (optional)"
              description="Paste the customer's existing test cases; the author reuses them where they cover a risk."
              autosize
              minRows={2}
              maxRows={8}
              value={existingCases}
              onChange={(e) => setExistingCases(e.currentTarget.value)}
            />
            <Group justify="flex-end">
              <Button
                leftSection={<IconChecklist size={16} />}
                loading={busy === "tests"}
                onClick={() => void author()}
              >
                Re-author tests
              </Button>
            </Group>
            {busy === "tests" && (
              <Text size="xs" c="dimmed" ta="right">
                Writing test scripts from the plan — this can take a minute.
              </Text>
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
